import { useEffect, useState } from "react";
import { useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import { getBlob, getJson, postJson, putJson } from "./client";

export type PeriodStatus =
  | "PENDING_SUBMISSION"
  | "PENDING_REVIEW"
  | "APPROVED"
  | "RETURNED"
  | "SUPERSEDED";

/**
 * One sign-off: an investor's facility for one month-end. Every row is submitted and approved on its own, and stays in the "awaiting"
 * table until it is approved, then moves to the Signed-off log. A row from before the monthly sign-offs (a whole-book period) has no
 * investor, facility or month and reads "All investors" / "All facilities".
 */
export interface AttestationPeriod {
  /** "WAFRA:CAP1:2026-10" */
  periodId: string;
  /** the month as "Oct-26" */
  periodLabel: string | null;
  /** "2026-10" */
  periodMonth: string | null;
  /** the last day of the month, yyyy-MM-dd */
  monthEnd: string | null;
  /** the month-end snapshot the figures are read at */
  snapshotDate: string | null;
  investorCode: string | null;
  investorName: string;
  facilityCode: string | null;
  facilityName: string;
  status: PeriodStatus;
  /** whole days since the month ended; null once the row is signed off */
  pendingDays: number | null;
  /** true on a row still to be signed off whose month-end counts can be asked for (see useAttestationStore: they load row by row) */
  countsAvailable: boolean;
  /** limit changes still waiting for approval in Manage Limits, for this investor and facility */
  pendingChanges: number;
  manageHref: string | null;
  submittedByRole?: string | null;
  submittedAt?: string | null;
  submitterNotes?: string | null;
  reviewedByRole?: string | null;
  reviewedAt?: string | null;
  reviewerNotes?: string | null;
  effectiveFrom?: string | null;
  effectiveTo?: string | null;
  /** false for a row with no month-end snapshot to download */
  downloadable: boolean;
  /** the row's three files, in order: the summary (or, for Internal, its limits), contract detail, ineligible contracts */
  files: AttestationFile[];
}

/** The month-end Red / Amber / Green counts of a row, or why they could not be worked out. */
type CountsResponse = { periodId: string } & (
  | { red: number; amber: number; green: number; notMeasured: number; attention: string[] }
  | { error: string }
);

/** A row still to be signed off, with its counts as they arrive: null until then, and for good where they could not be worked out. */
export interface AttestationRow extends AttestationPeriod {
  red: number | null;
  amber: number | null;
  green: number | null;
  notMeasured: number | null;
  attention: string[] | null;
  /** why the counts are missing, when they could not be worked out */
  countsError: string | null;
  /** true while the counts are still on their way (queued or being worked out) */
  countsLoading: boolean;
}

/** One downloadable table of a row. `kind` is what the download endpoint takes; `label` is what the menu says. */
export interface AttestationFile {
  kind: "summary" | "limits" | "contract-detail" | "ineligible-contracts";
  label: string;
}

const P = "portfolio-risk-concentrations";
const fetchPeriods = () => getJson<AttestationPeriod[]>(`${P}/attestation/periods`);

/** Rows still to be signed off, as against the signed-off log. A returned row goes back to the submitter and stays on the first list. */
export const isOpen = (p: AttestationPeriod) =>
  p.status === "PENDING_SUBMISSION" || p.status === "PENDING_REVIEW" || p.status === "RETURNED";

/** How many rows' counts are worked out at once. Each is Monitor-sized work on the server (the Internal ones are heavy), so the rest wait
 *  their turn instead of all being asked for together. */
const CONCURRENT_COUNTS = 3;

export function useAttestationStore() {
  const qc = useQueryClient();
  const periodsQ = useQuery({ queryKey: ["prc-attestation"], queryFn: fetchPeriods });

  const invalidate = () => qc.invalidateQueries({ queryKey: ["prc-attestation"] });

  const periods = periodsQ.data ?? [];

  // The counts are NOT in the list (it would be one long request, and it timed out at the gateway): each open row asks for its own, a few
  // at a time, and its cells fill in as the answer comes. A different query key from the list's, so approving a row does not refetch them.
  const wantsCounts = periods.filter((p) => isOpen(p) && p.countsAvailable);
  const [allowed, setAllowed] = useState(CONCURRENT_COUNTS);
  const countQueries = useQueries({
    queries: wantsCounts.map((p, i) => ({
      queryKey: ["prc-attestation-counts", p.periodId],
      queryFn: () => getJson<CountsResponse>(`${P}/attestation/counts?periodId=${encodeURIComponent(p.periodId)}`),
      enabled: i < allowed,
      staleTime: 5 * 60_000,
      retry: false,
    })),
  });
  const settled = countQueries.filter((q) => q.isSuccess || q.isError).length;
  useEffect(() => {
    setAllowed(CONCURRENT_COUNTS + settled);
  }, [settled]);
  const countsById = new Map(wantsCounts.map((p, i) => [p.periodId, countQueries[i]]));

  // oldest month first, so the one that has waited longest is on top (the server sends them in that order)
  const open: AttestationRow[] = periods.filter(isOpen).map((p) => {
    const q = countsById.get(p.periodId);
    const data = q?.data;
    const ok = data && !("error" in data) ? data : null;
    return {
      ...p,
      red: ok ? ok.red : null,
      amber: ok ? ok.amber : null,
      green: ok ? ok.green : null,
      notMeasured: ok ? ok.notMeasured : null,
      attention: ok ? ok.attention : null,
      countsError: data && "error" in data ? data.error : q?.isError ? (q.error instanceof Error ? q.error.message : "Could not load the counts.") : null,
      countsLoading: !!q && !q.isSuccess && !q.isError,
    };
  });
  // newest sign-off first
  const signedOff = periods
    .filter((p) => !isOpen(p))
    .sort((a, b) => (b.reviewedAt ?? b.effectiveFrom ?? "").localeCompare(a.reviewedAt ?? a.effectiveFrom ?? ""));

  const submit = (periodId: string, notes: string) =>
    postJson(`${P}/attestation/submit`, { periodId, notes }).then(invalidate);
  const decide = (periodId: string, decision: "APPROVED" | "RETURNED", notes: string) =>
    postJson(`${P}/attestation/decide`, { periodId, decision, notes }).then(invalidate);

  return {
    periods,
    open,
    signedOff,
    isLoading: periodsQ.isLoading,
    isError: periodsQ.isError,
    error: periodsQ.error,
    submit,
    decide,
  };
}

/** One investor x facility and its switch: ON lists it under Awaiting attestation, OFF leaves it out. */
export interface AttestationScopeRow {
  investorCode: string;
  investorName: string;
  facilityCode: string;
  facilityName: string;
  enabled: boolean;
  /** when the switch was last turned on or off; null until it has been used (the pair is ON until then) */
  updatedAt: string | null;
  updatedBy: string | null;
}

/** The switch of every investor x facility, and the call that turns one on or off. Turning one over also refreshes the Awaiting list. */
export function useAttestationScope() {
  const qc = useQueryClient();
  const query = useQuery({ queryKey: ["prc-attestation-scope"], queryFn: () => getJson<AttestationScopeRow[]>(`${P}/attestation/scope`) });
  const setEnabled = (row: Pick<AttestationScopeRow, "investorCode" | "facilityCode">, enabled: boolean) =>
    putJson(`${P}/attestation/scope`, { investorCode: row.investorCode, facilityCode: row.facilityCode, enabled }).then(() =>
      Promise.all([
        qc.invalidateQueries({ queryKey: ["prc-attestation-scope"] }),
        qc.invalidateQueries({ queryKey: ["prc-attestation"] }),
      ]),
    );
  return { rows: query.data ?? [], isLoading: query.isLoading, isError: query.isError, error: query.error, setEnabled };
}

/** A failed blob request carries its JSON error as a Blob: this reads the `detail` out of it. */
async function blobErrorDetail(err: unknown, fallback: string): Promise<string> {
  const data = (err as { response?: { data?: unknown } })?.response?.data;
  if (data instanceof Blob) {
    try {
      const detail = JSON.parse(await data.text())?.detail;
      if (typeof detail === "string" && detail) return detail;
    } catch {
      // not JSON: fall through to the generic message
    }
  }
  const detail = (err as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
  if (typeof detail === "string" && detail) return detail;
  return err instanceof Error && err.message ? err.message : fallback;
}

/**
 * Downloads ONE of a row's three tables (the Snapshot page's Portfolio Concentration Summary, Contract Detail or Ineligible Contracts; an
 * Internal row has its limits in place of the summary) as a CSV. The server streams it from a cursor; it is fetched through axios so the
 * Keycloak bearer is attached, and saved client-side. Throws an Error carrying the server's reason.
 */
export async function downloadAttestationFile(periodId: string, file: AttestationFile): Promise<void> {
  let result: { blob: Blob; contentDisposition: string | undefined };
  try {
    result = await getBlob(`${P}/attestation/download`, { periodId, kind: file.kind });
  } catch (err) {
    throw new Error(`${file.label}: ${await blobErrorDetail(err, "Could not download the file.")}`);
  }
  const match = result.contentDisposition ? /filename="?([^"]+)"?/.exec(result.contentDisposition) : null;
  const filename = match ? match[1] : `attestation-${periodId.replace(/:/g, "-")}-${file.kind}.csv`;
  const blobUrl = window.URL.createObjectURL(result.blob);
  const a = document.createElement("a");
  a.href = blobUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(blobUrl);
}

/** All of a row's files, one after another: each starts when the one before has been saved, and the first failure stops the rest. */
export async function downloadAttestationFiles(row: Pick<AttestationPeriod, "periodId" | "files">): Promise<void> {
  for (const file of row.files) {
    await downloadAttestationFile(row.periodId, file);
  }
}
